import { useNavigate } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { TreeGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import { getAudienceEditPath } from '@/components/audiences/is-simplified-compatible-filter-tree';
import { AudienceFilterTreeReadOnly } from '@/components/audiences/audience-filter-tree-read-only';
import { AudienceFilterDonut } from '@/components/audiences/audience-filter-donut';
import { Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AudienceFilterSectionProps {
  tenantSlug: string;
  audienceId: string;
  filterTree: TreeGroupNode | null;
  donutTargetUsersCount?: number;
  donutTotalUserbaseCount: number;
  isDonutLoading?: boolean;
  locale: Locales;
  className?: string;
}

export function AudienceFilterSection({
  tenantSlug,
  audienceId,
  filterTree,
  donutTargetUsersCount,
  donutTotalUserbaseCount,
  isDonutLoading = false,
  locale,
  className,
}: AudienceFilterSectionProps) {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const canEdit = filterTree !== null;

  const handleEdit = () => {
    if (!filterTree) return;
    navigate(getAudienceEditPath(tenantSlug, audienceId, filterTree));
  };

  return (
    <Card className={cn('border-secondary border-4 bg-secondary', className)}>
      <CardHeader className="p-4 bg-secondary text-secondary-foreground">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Filter data-icon="inline-start" className="size-4" />
            {LL.audiences.details.overview.filterConditions.title()}
          </CardTitle>
          {canEdit ? (
            <Button
              type="button"
              variant="ghost-primary"
              size="compact"
              className="shrink-0"
              onClick={handleEdit}
            >
              {LL.audiences.details.overview.filterConditions.edit()}
            </Button>
          ) : null}
        </div>
        <CardDescription>
          {LL.audiences.details.overview.filterConditions.description()}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 rounded bg-background pr-20">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <div className="min-w-0 flex-1">
            <AudienceFilterTreeReadOnly root={filterTree} />
          </div>
          <AudienceFilterDonut
            isLoading={isDonutLoading}
            targetUsersCount={donutTargetUsersCount}
            totalUserbaseCount={donutTotalUserbaseCount}
            locale={locale}
          />
        </div>
      </CardContent>
    </Card>
  );
}
